<template>
  <smd-one-col-layout class="order-chat"
                      content-styles="hide-overflow full-width">
    <smd-header slot="header"
                :nav="$isDesktop">
      <smd-btn class="back-btn"
               icon="ns-icon-ico028"
               slot="left-actions"
               @click="backToMessages"/>
      <h2 class="Title3 gray-400 text-center"
          v-if="order"
          :class="{'m-l-20': !$isDesktop}">
        <text-string label-id="txt190" :props="{strings: [order.orderrefno]}"/>
      </h2>
      <span class="ns-icon-ico001"
            slot="right-actions"
            v-if="$isMobile">
        <span class="path1"></span>
        <span class="path2"></span>
      </span>
    </smd-header>

    <div class="order-chat__view-details">
      <router-link :to="orderDetailsRoute">
        <text-string label-id="txt191" class="button"/>
      </router-link>
    </div>

    <smd-one-col-layout class="chat-container"
                        scrollable-content-id="scroll-el">
      <p v-if="error"
         class="above error">
        {{ error }}
      </p>

      <div v-if="loading"
           class="above">
        <smd-loading-spinner/>
      </div>

      <div class="messages">
        <div class="empty-state"
             v-if="!reverseMessages.length">
          <p class="gray-400">No messages shared yet.</p>
        </div>
        <div class="message-container"
             v-for="message in reverseMessages"
             :class="computedClass(message)"
             :key="message.id">
          <message-profile-pic v-if="!isPatientMessage(message)"
                               :profile-pic="$options.Ico001"
                               name="Now Serving"/>

          <component :from-patient="isPatientMessage(message)"
                     :is="activeComponent(message.type)"
                     :message="message"
                     @loaded="imageLoaded"
                     @emitSendProofOfPayment="selectFiles"/>
        </div>
      </div>
    </smd-one-col-layout>

    <div v-if="!archived"
         class="inputs-container smd-form__row"
         slot="footer">
      <smd-btn class="attachment-btn"
               icon="ns-icon-attachment"
               @click="selectFiles"/>
      <input hidden
             type="file"
             :accept="$options.attachmentAccept"
             :ref="$options.attachmentRef"
             @change="onFileInput">
      <input maxlength="255"
             placeholder="Type message here..."
             type="text"
             v-model="message"
             @keyup.enter="send">
      <smd-btn class="send-btn"
               icon="ns-icon-send"
               @click="send"/>
    </div>
  </smd-one-col-layout>
</template>

<script>
  import Vue from 'vue';
  import moment from 'moment';

  import MessageProfilePic from '@/components/messages/MessageProfilePic';
  import OrderMessageFile from '@/components/messages/order-message-file';
  import OrderSummaryMessage from '@/components/messages/order-summary-message';
  import OrderStatusMessage from '@/components/messages/order-status-message';
  import OrderPaymentDetailsMessage from '@/components/messages/order-payment-details-message';
  import MessagePlainText from '@/components/messages/MessagePlainText';
  import socketMixin from '@/mixins/socket';
  import GetElementStringMixin from '@/mixins/get-element-string';
  import {mapActions, mapGetters} from 'vuex';
  import {v4 as uuidv4} from 'uuid';
  import {MessageService} from '@/services/messages';
  import {
    getMessageMomentDate,
  } from '@/utils/messages';
  import {MSG_TYPES, ATTACHMENT_ACCEPT} from '@/constants/inbox-messages';
  import {SOCKET_EVENT} from '@/constants/socket';
  import {
    GETTER_MEDICINE_ORDERS,
    GETTER_PAST_MEDICINE_ORDERS,
    ACTION_SET_ORDER_LAST_MESSAGE,
    ACTION_REMOVE_UNREAD_MESSAGE_COUNT,
  } from '@/store/types';
  import {
    MEDICINE_ORDER,
    MEDICINE_ORDER_ID_PARAM,
    MESSAGES_ROUTE,
    ORDER_CHAT_ROUTE_PARAM,
    ERROR_ROUTE,
  } from '@/router/routes';
  import Ico001 from '@/assets/images/ico001.svg';

  export default {
    name: 'order-chat',
    mixins: [
      socketMixin,
      GetElementStringMixin,
    ],
    components: {
      MessageProfilePic,
    },
    data() {
      return {
        token: null,
        message: '',
        messages: [],
        loading: false,
        error: null,
        errorTimeoutRef: null,
        scrollEl: null,

        order: null,

        /**
         * primary used to scroll down when page is loaded
         * one case is when scrolled after pushing initial message,
         * images are still loading that causes to scroll up when loaded
         *
         * so the solution is if the image is loaded on component,
         * scroll down again
         *
         * change to false if:
         * - user tries to fetch past message
         * - received new message
         * - send new message
         */
        onInitialState: true,

        socketListeners: [
          {
            event: SOCKET_EVENT.ORDER_MESSAGE,
            callback: this.appendNewMessage,
          },
        ],
      };
    },
    computed: {
      ...mapGetters({
        orders: GETTER_MEDICINE_ORDERS,
        archivedOrders: GETTER_PAST_MEDICINE_ORDERS,
      }),
      orderDetailsRoute() {
        if (!this.order) return {};

        return {
          name: MEDICINE_ORDER,
          params: {
            [MEDICINE_ORDER_ID_PARAM]: this.order.orderid,
          },
        };
      },
      reverseMessages() {
        return this.messages.slice().reverse();
      },
      lastMessageTimestamp() {
        if (!this.messages.length) {
          return (new Date()).getTime();
        }

        return moment(this.messages[this.messages.length - 1].date).valueOf();
      },
      archived() {
        if (!this.order) return false;
        return !!this.archivedOrders.find(({orderid}) => orderid === this.order.orderid);
      },
    },
    methods: {
      ...mapActions({
        setOrderLastMessage: ACTION_SET_ORDER_LAST_MESSAGE,
        resetOrderUnreadCount: ACTION_REMOVE_UNREAD_MESSAGE_COUNT,
      }),
      activeComponent(type) {
        switch (type) {
          case (this.$options.messageTypes.msg):
          case (this.$options.messageTypes.message):
            return MessagePlainText;
          case (this.$options.messageTypes.file):
            return OrderMessageFile;
          case (this.$options.messageTypes.ordersummary):
            return OrderSummaryMessage;
          case (this.$options.messageTypes.orderstatus):
            return OrderStatusMessage;
          case (this.$options.messageTypes.paymentdetails):
            return OrderPaymentDetailsMessage;
        }
      },
      backToMessages() {
        this.$router.push({name: MESSAGES_ROUTE});
      },
      computedClass(message) {
        return {
          'right': this.isPatientMessage(message),
          'image-file': message.msgtype === this.$options.messageTypes.attachment,
          'bill-message': message.msgtype === this.$options.messageTypes.pxbill
        }
      },
      initialize() {
        this.loading = true;

        if (this.order) {
          this.fetchMessages(true);
        } else {
          this.$router.push({name: ERROR_ROUTE});
        }
      },
      async fetchMessages(initialLoad = false) {
        this.loading = true;
        this.error = null;

        try {
          const lastMessageTimestamp = this.messages.length
            ? moment(getMessageMomentDate(this.messages[this.messages.length - 1])).valueOf()
            : (new Date()).getTime();
          const {data: messages} = await MessageService.getOrderMessage(this.order.orderid, lastMessageTimestamp);

          if (initialLoad) {
            messages.forEach((message) => {
              this.messages.push(message);
              // expected only to actually scroll to bottom if there's no images on messages
              // else, it will still try but scrolls up when images are loaded
              // on that case, the imageLoaded will trigger the scroll to bottom again
              this.scrollToBottom(true);
            });

            this.messages.push({
              from: '0',
              date: this.order.daterequested,
              msg: this.getElementString('txt198'),
              type: this.$options.messageTypes.msg,
            });
          } else {
            messages.forEach((message) => {
              if (getMessageMomentDate(message) < lastMessageTimestamp) {
                this.messages.push(message);
              }
            });
          }

          this.resetOrderUnreadCount(this.order.orderid);
          this.loading = false;
        } catch (error) {
          this.loading = false;
          this.error = 'Failed to fetch your messages.';
        }
      },
      selectFiles() {
        this.$refs[this.$options.attachmentRef].click();
      },
      async onFileInput(e) {
        const file = e.target.files[0];

        if (!file) {
          return;
        }

        const maxFileSize = 10485760; // 10 MB

        if (file.size > maxFileSize) {
          this.$refs[this.$options.attachmentRef].value = null;
          await this.$alert.show({
            text: 'Your file has exceed the maximum size of 10 MB',
            buttons: [
              {value: null, label: 'Okay'},
            ],
          });
          return;
        }

        // a 0-byte file is truthy and passes the size cap, but uploads an empty
        // multipart part the API cannot tell apart from no file at all
        if (!file.size) {
          this.$refs[this.$options.attachmentRef].value = null;
          await this.$alert.show({
            text: 'That file is empty. Please pick another file.',
            buttons: [
              {value: null, label: 'Okay'},
            ],
          });
          return;
        }

        const message = this.message;
        this.message = '';

        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('mimetype', file.type);
          formData.append('msg', message);
          formData.append('clientRefId', uuidv4());

          const {data} = await MessageService.sendOrderAttachmentMessage(this.order.orderid, formData);

          if (!data) {
            throw Error();
          }

          this.$refs[this.$options.attachmentRef].value = null;
        } catch (error) {
          this.$refs[this.$options.attachmentRef].value = null;
          this.error = `Failed to upload ${file.name}.`;
        }
      },
      appendSharedFile(data) {
        if (data.orderid === this.order.orderid) {
          if (data.mimetype) {
            this.messages.unshift({
              ...data,
              msgtype: MSG_TYPES.file,
            });
          }
        }

        this.scrollToBottom(true);
      },
      async send() {
        if (this.message === '') {
          return;
        }

        const message = this.message;
        this.message = '';

        try {
          const {data} = await MessageService.sendOrderMessage(this.order.orderid, message, uuidv4());

          if (!data) {
            throw Error();
          }
        } catch (error) {
          this.error = 'There was an error sending your message. Please try again.';
        }
      },
      appendNewMessage(data) {
        if (data.orderid === this.order.orderid) {
          /**
           * message received tend to receive a duplicate because `order message` event is emitted
           * both via account and token channel so ignore the 2nd one if received
           */
          const messageAlreadyReceived = this.messages.find(({id}) => id === data.id);

          if (messageAlreadyReceived) return;

          this.messages.unshift(data);
          this.setOrderLastMessage(data);
        }

        this.scrollToBottom(true);
      },
      scrollToBottom(onNextTick = false) {
        if (onNextTick) {
          Vue.nextTick(() => {
            this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
          });
        } else {
          this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
        }
      },
      handleScrollToTop() {
        const {scrollTop} = this.scrollEl;

        if (scrollTop === 0) {
          this.onInitialState = false;
          this.fetchMessages();
        }
      },
      isPatientMessage({from}) {
        return from === '1';
      },
      imageLoaded() {
        if (this.onInitialState) {
          this.scrollToBottom();
        }
      },
    },
    watch: {
      orders: {
        handler(orders) {
          if (orders && !this.order) {
            this.order = orders.find(({orderid}) => orderid === this.$route.params[ORDER_CHAT_ROUTE_PARAM]);
            this.initialize();
          }
        },
        immediate: true,
      },
      error(error) {
        if (error) {
          if (this.errorTimeoutRef) {
            clearTimeout(this.errorTimeoutRef);
          }

          this.errorTimeoutRef = setTimeout(() => {
            this.error = null;
          }, 2000);
        }
      },
    },
    mounted() {
      this.scrollEl = document.getElementById('scroll-el');
      this.scrollEl.addEventListener('scroll', this.handleScrollToTop);
    },
    beforeDestroy() {
      this.scrollEl.removeEventListener('scroll', this.handleScrollToTop);
    },
    attachmentRef: 'attachmentRef',
    attachmentAccept: ATTACHMENT_ACCEPT,
    messageTypes: MSG_TYPES,
    Ico001,
  }
</script>

<style lang="scss">
  .order-chat {
    .header {
      padding: space(s);

      h2 {
        width: 100%;
      }
    }

    &__view-details {
      position: relative;
      z-index: 1;

      .button {
        width: 100%;
        padding: 2px space(s);
        text-align: center;
        background-color: color(purple, brand);
      }
    }

    .smd-profile-image {
      background-color: transparent;
    }

    .chat-container {
      .messages {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        min-height: 100%;

        @include responsive(desktop) {
          padding: space(s);
        }
      }

      .above {
        position: sticky;
        top: 0;
        z-index: 1;
        margin-bottom: auto;
        padding: space(xs) space(m);
      }

      .error {
        text-align: center;
        color: color(white, text);
        padding: space(xs) space(m);
        background-color: color(red, background);
      }

      .message-container {
        display: flex;
        max-width: 84%;

        &.bill-message {
          max-width: 95%;
        }

        &.image-file {
          max-width: 45%;
        }

        &.right {
          display: block;
          margin-left: auto;
        }

        &:first-child {
          margin-top: space(s);
        }

        &:not(:last-child) {
          margin-bottom: space(s);
        }
      }
    }

    .inputs-container {
      display: flex;
      align-items: center;
      padding: space(s);
      background-color: color(base, background);
      z-index: 100;

      input {
        flex: 1;
        height: 32px;
        padding: space(s) space(m);
        margin: 0 space(m);
        border: none;
        outline: none;
        box-shadow: none;
        border-radius: 16px;
        background-color: color(dark, background);
      }

      .send-btn {
        color: color(blue, brand);
      }
    }

    .empty-state {
      text-align: center;
      margin: auto;
    }
  }
</style>
